import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, BarChart3 } from "lucide-react";
import { apiFetch } from "./api/client";
import { UI } from "./designSystem";
import { getEffectiveRoles } from "./roleSimulator";

// Tela /admin/analytics -- pedido do Rodrigo (21/10/2026): "pode trazer
// dados do analytics para uma pagina no administrativo? principalmente
// para vermos volume de acessos em paginas, ebooks e devocionais". Puxa
// da Google Analytics Data API (GA4) via GET /admin/analytics/overview
// (ver mosaico-api/src/lib/ga4.js). Mesmo padrão de filtro de período e
// de gate ADMIN-only de AdminAgentRelatorio.jsx (ver esse arquivo pra
// contexto dos atalhos de data).
const ADMIN_PATH = "/admin";

// Rotulo amigavel pra cada caminho conhecido do app -- antes a tabela
// mostrava o pageTitle cru que o GA4 capturava, que quase nunca batia com
// a pagina de verdade (a SPA nem sempre atualiza o <title> do navegador
// por rota, ver comentario em mosaico-api/src/lib/ga4.js) e ainda causava
// linhas duplicadas pro mesmo caminho. Agora o backend soma tudo por
// pagePath e aqui so' trocamos o rotulo exibido -- nunca o caminho em si
// (pedido da Carla, 24/10/2026: "nao e possivel renomea-las?"). Caminho
// sem entrada aqui cai no fallback (mostra so' o path cru).
const PAGE_LABELS = {
  "/": "Site institucional (Home)",
  "/sobre": "Sobre a igreja",
  "/ministerios": "Ministérios",
  "/pregacoes": "Pregações",
  "/eventos": "Agenda de eventos",
  "/contato": "Contato",
  "/perguntas-frequentes": "Perguntas frequentes",
  "/devocionais": "Devocionais",
  "/entrar": "Login",
  "/cadastro/membro": "Autocadastro de membro",
  "/cadastro/crianca": "Autocadastro de criança/adolescente",
  "/familia": "Área da família (app, logado)",
  "/admin": "Painel administrativo (início)",
  "/admin/usuarios": "Administração de usuários",
  "/admin/membros": "Gestão de membros",
  "/admin/membros-consulta": "Consulta de membros",
  "/admin/criancas": "Gestão de crianças/adolescentes",
  "/admin/criancas-consulta": "Consulta de crianças/adolescentes",
  "/admin/visitantes": "Gestão de visitantes (Conecta)",
  "/admin/acolhimento": "Acolhimento",
  "/admin/repertorio": "Repertório (admin)",
  "/admin/escalas": "Escalas",
  "/repertorio": "Repertório (músico)",
  "/admin/aniversariantes": "Aniversariantes",
  "/admin/agente": "Configuração do agente Barnabé",
  "/admin/agente-relatorio": "Relatório do agente Barnabé",
  "/admin/analytics": "Analytics"
};

function labelForPath(path) {
  // Caminhos dentro de /familia/* reaproveitam a mesma tela de fora (ver
  // rotas espelhadas em index.js), entao o rotulo e' o mesmo tirando o
  // prefixo "/familia".
  const semPrefixo = path.startsWith("/familia") ? path.slice("/familia".length) || "/" : path;
  return PAGE_LABELS[semPrefixo] || PAGE_LABELS[path] || null;
}

// Colunas da tabela "Páginas mais acessadas", reaproveitadas nas duas
// tabelas (site e admin, ver mais abaixo -- pedido da Carla, 24/10/2026:
// "nao seria ideal separar mais acessadas do admin, do site?").
const PAGE_COLUMNS = [
  {
    key: "path",
    label: "Página",
    render: (row) => {
      // Rotulo amigavel (PAGE_LABELS acima) como info principal, com o
      // caminho cru embaixo, menor, so' como referencia tecnica -- antes
      // era o contrario (path em cima, pageTitle cru do GA4 embaixo), que
      // ficava confuso e ainda duplicava linha pro mesmo caminho quando o
      // GA4 capturava titulos diferentes (pedido da Carla, 24/10/2026:
      // "nao e possivel renomea-las?" -- ver tambem o backend, que agora
      // soma por pagePath e separa site de admin).
      const label = labelForPath(row.path);
      return (
        <div>
          <div style={{ color: "#F8FAFC", fontWeight: 600 }}>{label || row.path}</div>
          {label ? <div style={{ fontSize: 11, color: "#7c8db0", marginTop: 2 }}>{row.path}</div> : null}
        </div>
      );
    }
  },
  { key: "views", label: "Visualizações", align: "right" },
  { key: "users", label: "Usuários", align: "right" }
];

const pageStyle = {
  minHeight: "100vh",
  width: "100%",
  boxSizing: "border-box",
  backgroundColor: UI.shell,
  color: UI.text,
  fontFamily: "'DM Sans', system-ui, sans-serif",
  padding: "28px 20px 60px"
};

const backLinkStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  color: "#9fb0cc",
  fontSize: 13,
  fontWeight: 700,
  textDecoration: "none",
  marginBottom: 18
};

const cardStyle = {
  background: "linear-gradient(160deg, #0f1727, #131f34)",
  border: "1px solid rgba(120,152,210,0.28)",
  borderRadius: 16,
  padding: 18,
  boxShadow: "0 14px 28px rgba(0,0,0,0.36), inset 0 1px 0 rgba(255,255,255,0.06)",
  marginBottom: 16
};

const sectionTitleStyle = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  margin: "0 0 12px",
  fontSize: 14,
  fontWeight: 800,
  color: "#F8FAFC",
  textTransform: "uppercase",
  letterSpacing: 0.4
};

const inputStyle = {
  border: "1px solid rgba(226,232,240,0.16)",
  background: "rgba(12,20,34,0.68)",
  color: "#F8FAFC",
  borderRadius: 10,
  padding: "9px 11px",
  fontSize: 13,
  fontFamily: "inherit",
  boxSizing: "border-box",
  outline: "none",
  colorScheme: "dark"
};

const buttonStyle = {
  border: "1px solid rgba(230,177,90,0.45)",
  background: "rgba(230,177,90,0.14)",
  color: UI.brand,
  borderRadius: 10,
  padding: "9px 16px",
  fontSize: 13,
  fontWeight: 800,
  cursor: "pointer",
  fontFamily: "inherit"
};

// Mesmos atalhos de AdminAgentRelatorio.jsx -- pra manter familiaridade
// com quem já usa aquela tela.
const DATE_RANGE_PRESETS = [
  { id: "hoje", label: "Hoje" },
  { id: "ontem", label: "Ontem" },
  { id: "7dias", label: "Últimos 7 dias" },
  { id: "30dias", label: "Últimos 30 dias" },
  { id: "mes", label: "Este mês" }
];

const presetButtonStyle = (active) => ({
  border: active ? "1px solid rgba(230,177,90,0.55)" : "1px solid rgba(226,232,240,0.18)",
  background: active ? "rgba(230,177,90,0.18)" : "rgba(12,20,34,0.5)",
  color: active ? UI.brand : "#c7ced9",
  borderRadius: 999,
  padding: "6px 12px",
  fontSize: 12,
  fontWeight: 700,
  cursor: "pointer",
  fontFamily: "inherit",
  whiteSpace: "nowrap"
});

const statStyle = { textAlign: "center" };
const statNumberStyle = { margin: 0, fontSize: 28, fontWeight: 800, color: UI.brand };
const statLabelStyle = { margin: "2px 0 0", fontSize: 12, color: "#9fb0cc" };

const tableWrapStyle = { overflowX: "auto" };
const thStyle = {
  textAlign: "left",
  fontSize: 11,
  color: "#9fb0cc",
  textTransform: "uppercase",
  letterSpacing: 0.4,
  padding: "0 10px 8px",
  borderBottom: "1px solid rgba(226,232,240,0.12)"
};
const tdStyle = { padding: "8px 10px", fontSize: 13, color: "#E2E8F0", borderBottom: "1px solid rgba(226,232,240,0.06)" };
const tdNumStyle = { ...tdStyle, textAlign: "right", fontVariantNumeric: "tabular-nums" };

// Fuso de Sao Paulo pros atalhos de data -- mesmo raciocinio/código de
// AdminAgentRelatorio.jsx (a igreja é em Osasco/SP; "hoje"/"ontem" tem que
// bater com o dia em São Paulo, não com UTC nem com o fuso de quem está
// logado).
const TZ = "America/Sao_Paulo";
function fmtDateInput(d) {
  const parts = new Intl.DateTimeFormat("en-CA", { timeZone: TZ, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(d);
  const map = Object.fromEntries(parts.map((p) => [p.type, p.value]));
  return `${map.year}-${map.month}-${map.day}`;
}

// "jardim-evangelho" -> "Jardim Evangelho" -- não temos um catálogo de
// títulos de e-book/trilha no backend (ver comentário em
// EbookReadingState no schema: o catálogo é só no front, data/ebooks.json),
// então aqui é só um rótulo legível a partir do slug; Rodrigo reconhece o
// conteúdo pelo nome mesmo formatado assim.
function labelFromSlug(slug) {
  if (!slug) return "";
  return slug
    .split("-")
    .map((part) => (part ? part[0].toUpperCase() + part.slice(1) : part))
    .join(" ");
}

// Tabela com "drill-down": uma linha fechada por trilha (soma de todas as
// paginas dela -- visao geral + cada dia), clica e abre o detalhe por dia
// embaixo (pedido da Carla, 24/10/2026: "poderia ter um drill down? faz a
// lista fechada pelo devocional e se quiser detalhe clica pra abrir os
// dias" -- a lista de devocionais crescia muito porque cada trilha aparecia
// espalhada em varias linhas, uma por dia).
function TrailDrilldownTable({ rows, emptyLabel }) {
  const [expanded, setExpanded] = useState(() => new Set());

  if (!rows.length) {
    return <p style={{ margin: 0, fontSize: 13, color: "#7c8db0", fontStyle: "italic" }}>{emptyLabel}</p>;
  }

  const groups = new Map();
  for (const row of rows) {
    if (!groups.has(row.trailSlug)) {
      groups.set(row.trailSlug, { trailSlug: row.trailSlug, views: 0, users: 0, details: [] });
    }
    const group = groups.get(row.trailSlug);
    group.views += row.views;
    group.users += row.users;
    group.details.push(row);
  }
  const groupList = Array.from(groups.values()).sort((a, b) => b.views - a.views);
  for (const group of groupList) {
    // "Visao geral" (day null) primeiro, depois Dia 1, 2, 3...
    group.details.sort((a, b) => (a.day || 0) - (b.day || 0));
  }

  function toggle(slug) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  }

  return (
    <div style={tableWrapStyle}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={thStyle}>Trilha</th>
            <th style={{ ...thStyle, textAlign: "right" }}>Visualizações</th>
            <th style={{ ...thStyle, textAlign: "right" }}>Usuários</th>
          </tr>
        </thead>
        <tbody>
          {groupList.map((group) => {
            const isOpen = expanded.has(group.trailSlug);
            return (
              <React.Fragment key={group.trailSlug}>
                <tr onClick={() => toggle(group.trailSlug)} style={{ cursor: "pointer" }}>
                  <td style={tdStyle}>
                    <span
                      style={{
                        display: "inline-block",
                        width: 12,
                        marginRight: 6,
                        transform: isOpen ? "rotate(90deg)" : "none",
                        transition: "transform .12s",
                        color: "#7c8db0"
                      }}
                    >
                      ▸
                    </span>
                    {labelFromSlug(group.trailSlug)}
                    <span style={{ marginLeft: 8, fontSize: 11, color: "#7c8db0" }}>
                      ({group.details.length} {group.details.length === 1 ? "página" : "páginas"})
                    </span>
                  </td>
                  <td style={tdNumStyle}>{group.views}</td>
                  <td style={tdNumStyle}>{group.users}</td>
                </tr>
                {isOpen
                  ? group.details.map((detail, i) => (
                      <tr key={i} style={{ background: "rgba(255,255,255,0.03)" }}>
                        <td style={{ ...tdStyle, paddingLeft: 34, fontSize: 12, color: "#c7ced9" }}>
                          {detail.day ? `Dia ${detail.day}` : "Visão geral"}
                        </td>
                        <td style={tdNumStyle}>{detail.views}</td>
                        <td style={tdNumStyle}>{detail.users}</td>
                      </tr>
                    ))
                  : null}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function CenteredMessage({ children }) {
  return (
    <div style={pageStyle}>
      <div style={{ maxWidth: 480, margin: "18vh auto 0", textAlign: "center" }}>
        <p style={{ margin: 0, color: "#c7ced9", fontSize: 14, lineHeight: 1.6 }}>{children}</p>
        <Link to={ADMIN_PATH} style={{ ...backLinkStyle, marginTop: 18, justifyContent: "center" }}>
          <ArrowLeft size={14} /> Voltar
        </Link>
      </div>
    </div>
  );
}

function RankedTable({ columns, rows, emptyLabel }) {
  if (!rows.length) {
    return <p style={{ margin: 0, fontSize: 13, color: "#7c8db0", fontStyle: "italic" }}>{emptyLabel}</p>;
  }
  return (
    <div style={tableWrapStyle}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col.key} style={{ ...thStyle, ...(col.align === "right" ? { textAlign: "right" } : {}) }}>
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              {columns.map((col) => (
                <td key={col.key} style={col.align === "right" ? tdNumStyle : tdStyle}>
                  {col.render ? col.render(row) : row[col.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AdminAnalytics() {
  const [status, setStatus] = useState("checking");
  const [viewerUser, setViewerUser] = useState(null);

  const today = new Date();
  const sevenDaysAgo = new Date(today.getTime() - 6 * 24 * 3600 * 1000);
  const [fromInput, setFromInput] = useState(fmtDateInput(sevenDaysAgo));
  const [toInput, setToInput] = useState(fmtDateInput(today));
  const [activePreset, setActivePreset] = useState("7dias");

  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState("");

  useEffect(() => {
    let alive = true;
    apiFetch("/auth/me", { method: "GET", skipSessionExpiredHandler: true })
      .then((me) => {
        if (!alive) return;
        if (getEffectiveRoles(me).includes("ADMIN") && !me.passwordMustChange) {
          setViewerUser(me);
          setStatus("ready");
        } else {
          setStatus("forbidden");
        }
      })
      .catch(() => {
        if (alive) setStatus("forbidden");
      });
    return () => {
      alive = false;
    };
  }, []);

  const canManage = status === "ready" && Boolean(viewerUser?.id);

  const loadOverview = async (overrides) => {
    if (!canManage) return;
    const effectiveFrom = overrides?.from ?? fromInput;
    const effectiveTo = overrides?.to ?? toInput;
    setLoading(true);
    setFeedback("");
    try {
      const params = new URLSearchParams({ from: effectiveFrom, to: effectiveTo });
      const data = await apiFetch(`/admin/analytics/overview?${params.toString()}`, { method: "GET", viewer: viewerUser });
      setOverview(data);
    } catch (error) {
      setFeedback("Não foi possível carregar os dados do Analytics agora.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canManage) loadOverview();
  }, [canManage]);

  const applyDateRangePreset = (presetId) => {
    const now = new Date();
    let from = now;
    let to = now;
    if (presetId === "ontem") {
      const yesterday = new Date(now.getTime() - 86400000);
      from = yesterday;
      to = yesterday;
    } else if (presetId === "7dias") {
      from = new Date(now.getTime() - 6 * 86400000);
    } else if (presetId === "30dias") {
      from = new Date(now.getTime() - 29 * 86400000);
    } else if (presetId === "mes") {
      const [spYear, spMonth] = fmtDateInput(now).split("-");
      from = new Date(`${spYear}-${spMonth}-01T00:00:00`);
    }
    const fromStr = fmtDateInput(from);
    const toStr = fmtDateInput(to);
    setActivePreset(presetId);
    setFromInput(fromStr);
    setToInput(toStr);
    loadOverview({ from: fromStr, to: toStr });
  };

  if (status === "checking") return <CenteredMessage>Carregando...</CenteredMessage>;
  if (status === "forbidden") return <CenteredMessage>Você não tem acesso a esta área.</CenteredMessage>;

  return (
    <div style={pageStyle}>
      <Link to={ADMIN_PATH} style={backLinkStyle}>
        <ArrowLeft size={14} /> Voltar
      </Link>

      <h1 style={{ margin: "0 0 4px", fontSize: 22, fontWeight: 800 }}>Analytics do site</h1>
      <p style={{ margin: "0 0 20px", fontSize: 13, color: "#9fb0cc" }}>
        Visível só pra quem é admin. Volume de acessos vindo do Google Analytics -- páginas do site, e-books e devocionais.
      </p>

      <div style={cardStyle}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
          {DATE_RANGE_PRESETS.map((preset) => (
            <button
              key={preset.id}
              type="button"
              style={presetButtonStyle(activePreset === preset.id)}
              onClick={() => applyDateRangePreset(preset.id)}
              disabled={loading}
            >
              {preset.label}
            </button>
          ))}
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-end" }}>
          <div>
            <label style={{ display: "block", fontSize: 11, color: "#9fb0cc", marginBottom: 5 }}>De</label>
            <input type="date" value={fromInput} onChange={(e) => { setActivePreset(null); setFromInput(e.target.value); }} style={inputStyle} />
          </div>
          <div>
            <label style={{ display: "block", fontSize: 11, color: "#9fb0cc", marginBottom: 5 }}>Até</label>
            <input type="date" value={toInput} onChange={(e) => { setActivePreset(null); setToInput(e.target.value); }} style={inputStyle} />
          </div>
          <button type="button" style={buttonStyle} onClick={() => loadOverview()} disabled={loading}>
            {loading ? "Atualizando..." : "Atualizar"}
          </button>
        </div>
      </div>

      {feedback ? <p style={{ color: "#FCA5A5", fontSize: 13 }}>{feedback}</p> : null}

      {overview && overview.configured === false ? (
        <div style={cardStyle}>
          <p style={{ margin: 0, fontSize: 13, color: "#c7ced9", lineHeight: 1.6 }}>
            A integração com o Google Analytics ainda não foi configurada neste ambiente (faltam as variáveis
            GA4_SERVICE_ACCOUNT_CLIENT_EMAIL, GA4_SERVICE_ACCOUNT_PRIVATE_KEY e GA4_PROPERTY_ID no backend).
          </p>
        </div>
      ) : null}

      {overview && overview.configured ? (
        <>
          <div style={{ ...cardStyle, display: "flex", gap: 24, justifyContent: "center" }}>
            <div style={statStyle}>
              <p style={statNumberStyle}>{overview.totals.activeUsers}</p>
              <p style={statLabelStyle}>Usuários ativos</p>
            </div>
            <div style={statStyle}>
              <p style={statNumberStyle}>{overview.totals.sessions}</p>
              <p style={statLabelStyle}>Sessões</p>
            </div>
            <div style={statStyle}>
              <p style={statNumberStyle}>{overview.totals.pageViews}</p>
              <p style={statLabelStyle}>Visualizações de página</p>
            </div>
          </div>

          <div style={cardStyle}>
            <h2 style={sectionTitleStyle}>
              <BarChart3 size={15} /> Páginas mais acessadas — Site
            </h2>
            <RankedTable emptyLabel="Sem dados no período." columns={PAGE_COLUMNS} rows={overview.topPagesSite} />
          </div>

          <div style={cardStyle}>
            <h2 style={sectionTitleStyle}>
              <BarChart3 size={15} /> Páginas mais acessadas — Painel administrativo
            </h2>
            <RankedTable emptyLabel="Sem dados no período." columns={PAGE_COLUMNS} rows={overview.topPagesAdmin} />
          </div>

          <div style={cardStyle}>
            <h2 style={sectionTitleStyle}>
              <BarChart3 size={15} /> E-books mais abertos
            </h2>
            <RankedTable
              emptyLabel="Sem dados no período."
              columns={[
                { key: "slug", label: "E-book", render: (row) => labelFromSlug(row.slug) },
                { key: "views", label: "Visualizações", align: "right" },
                { key: "users", label: "Usuários", align: "right" }
              ]}
              rows={overview.ebooks}
            />
          </div>

          <div style={cardStyle}>
            <h2 style={sectionTitleStyle}>
              <BarChart3 size={15} /> Devocionais mais acessados
            </h2>
            <TrailDrilldownTable emptyLabel="Sem dados no período." rows={overview.devocionais} />
          </div>
        </>
      ) : null}
    </div>
  );
}
